import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Login, Signup } from "@/pages/AuthPages";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordPages";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Muses from "@/pages/Muses";
import MuseWizard from "@/pages/MuseWizard";
import MuseDetail from "@/pages/MuseDetail";
import Account from "@/pages/Account";
import Create from "@/pages/Create";
import Library from "@/pages/Library";
import CardView from "@/pages/CardView";
import Landing from "@/pages/Landing";
import { Pricing, PaymentSuccess, PaymentCancel } from "@/pages/Billing";
import ExampleCard from "@/pages/ExampleCard";
import Planner from "@/pages/Planner";
import Admin from "@/pages/Admin";

const Splash = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
    <div className="font-display text-3xl accent-text animate-pulse">Certified Muse Studio</div>
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function Root() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Landing />;
  return <Layout><Home /></Layout>;
}

function PublicOnly({ children }) {
  const { user, next } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={next} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Root />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/example" element={<ExampleCard />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/planner" element={<Protected><Planner /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/muses" element={<Protected><Muses /></Protected>} />
      <Route path="/muses/new" element={<Protected><MuseWizard /></Protected>} />
      <Route path="/muses/:id/build" element={<Protected><MuseWizard /></Protected>} />
      <Route path="/muses/:id" element={<Protected><MuseDetail /></Protected>} />
      <Route path="/create" element={<Protected><Create /></Protected>} />
      <Route path="/library" element={<Protected><Library /></Protected>} />
      <Route path="/library/:id" element={<Protected><CardView /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="light" position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
